import { getFunctions, httpsCallable } from 'firebase/functions'
import app from '@/config/firebase'

// Singleton instance
let instance = null
let isInitialized = false

class AIAPIService {
  constructor() {
    // Return existing instance if it exists
    if (instance) {
      return instance
    }
    
    // Initialize Firebase Cloud Functions
    this.functions = getFunctions(app)
    
    // Initialize callable functions
    this.generateAIContentFn = httpsCallable(this.functions, 'generateAIContent')
    this.generateAIImageFn = httpsCallable(this.functions, 'generateAIImage')
    this.generateConversationalResponseFn = httpsCallable(this.functions, 'generateConversationalResponse')
    
    // Only log once on first initialization
    if (!isInitialized) {
      console.log('✅ AIAPIService initialized with secure Cloud Functions')
      isInitialized = true
    }
    
    // Store instance
    instance = this
  }

  // OpenAI API Integration via Cloud Function
  async generateContent(prompt, type = 'text', options = {}) {
    try {
      // Validate prompt
      if (!prompt || prompt.trim().length === 0) {
        return {
          success: false,
          error: 'Prompt is required for content generation'
        }
      }

      // Call secure Cloud Function
      const result = await this.generateAIContentFn({
        prompt: prompt.trim(),
        type,
        options
      })

      return result.data
    } catch (error) {
      console.error('Error generating content:', error)
      return {
        success: false,
        error: error.message || 'Failed to generate content'
      }
    }
  }

  // Image Generation via Cloud Function  
  async generateImage(prompt, options = {}) {
    try {
      // Validate prompt
      if (!prompt || prompt.trim().length === 0) {
        throw new Error('Prompt is required for image generation')
      }

      console.log('🎨 Generating image via secure Cloud Function...')
      
      // Call secure Cloud Function
      const result = await this.generateAIImageFn({
        prompt: prompt.trim(),
        options
      })

      console.log('✅ Image generated successfully')
      return result.data
    } catch (error) {
      console.error('Error generating image:', error)
      return {
        success: false,
        error: error.message || 'Failed to generate image'
      }
    }
  }

  // Conversational AI Response Generation via Cloud Function
  async generateConversationalResponse(userMessage, context = {}) {
    try {
      // Call secure Cloud Function
      const result = await this.generateConversationalResponseFn({
        userMessage,
        systemContext: context.systemContext
      })

      return result.data
    } catch (error) {
      console.error('Error generating conversational response:', error)
      return {
        success: false,
        error: error.message || 'Failed to generate response'
      }
    }
  }

  // Content Analysis - Use generateContent for analysis tasks
  async analyzeContent(content, analysisType = 'general') {
    const analysisPrompts = {
      general: 'Analyze this content for clarity, engagement, and effectiveness for digital signage displays.',
      marketing: 'Analyze this marketing content for persuasiveness, call-to-action effectiveness, and target audience appeal.',
      technical: 'Analyze this content for technical accuracy, readability, and information completeness.'
    }

    const prompt = `${analysisPrompts[analysisType]}\n\nContent: ${content}`
    
    const result = await this.generateContent(prompt, 'analysis', {
      systemPrompt: 'You are an expert content analyst specializing in digital signage and marketing communications.',
      maxTokens: 200,
      temperature: 0.3
    })

    if (result.success) {
      return {
        success: true,
        analysis: result.content
      }
    }
    
    return result
  }

  // Automated Content Creation
  async createAutomatedContent(businessType, contentType, options = {}) {
    try {
      const prompts = {
        restaurant: {
          menu: 'Create an engaging menu display for a restaurant with appetizing descriptions and clear pricing.',
          promotion: 'Create a promotional banner for restaurant specials with compelling call-to-action.',
          welcome: 'Create a welcoming message for restaurant customers with warm, inviting tone.'
        },
        retail: {
          sale: 'Create a sale announcement banner with eye-catching design and clear discount information.',
          product: 'Create a product showcase display highlighting key features and benefits.',
          event: 'Create an event promotion banner for retail store events or sales.'
        },
        general: {
          info: 'Create an informational display with clear, concise messaging.',
          announcement: 'Create an announcement banner with important information.',
          welcome: 'Create a general welcome message for visitors.'
        }
      }

      const prompt = prompts[businessType]?.[contentType] || prompts.general.info
      
      // Generate text content
      const textResult = await this.generateContent(prompt, contentType, options)
      
      if (!textResult.success) {
        throw new Error(textResult.error)
      }

      // Generate matching image if needed
      let imageResult = null
      if (options.generateImage) {
        const imagePrompt = `Create a professional digital signage image for: ${prompt}`
        imageResult = await this.generateImage(imagePrompt, {
          size: '1024x1024',
          style: 'natural'
        })
      }

      // Save to database
      const contentData = {
        type: contentType,
        businessType: businessType,
        content: textResult.content,
        imageUrl: imageResult?.success ? imageResult.images[0] : null,
        createdAt: new Date().toISOString(),
        aiGenerated: true,
        options: options
      }

      return {
        success: true,
        content: contentData,
        textGeneration: textResult,
        imageGeneration: imageResult
      }
    } catch (error) {
      console.error('Error creating automated content:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Social Media Integration (placeholder for future implementation)
  async getSocialMediaContent(platform, options = {}) {
    // This would integrate with social media APIs like Twitter, Instagram, Facebook
    // Implementation depends on specific platform APIs and authentication
    return {
      success: false,
      error: 'Social media integration not yet implemented'
    }
  }

  // Analytics Enhancement - Use generateContent for analytics
  async enhanceAnalytics(analyticsData) {
    const prompt = `Analyze this digital signage analytics data and provide actionable insights and recommendations:\n\n${JSON.stringify(analyticsData, null, 2)}`
    
    const result = await this.generateContent(prompt, 'analytics', {
      systemPrompt: 'You are an expert data analyst specializing in digital signage performance optimization.',
      maxTokens: 300,
      temperature: 0.3
    })

    if (result.success) {
      return {
        success: true,
        insights: result.content
      }
    }
    
    return result
  }

  // API Health Check - Cloud Functions based
  async checkAPIHealth() {
    const healthStatus = {}
    
    // Test OpenAI via Cloud Function
    try {
      await this.generateContent('test', 'text', { maxTokens: 5 })
      healthStatus.openai = 'healthy'
    } catch (error) {
      console.error('OpenAI health check failed:', error)
      healthStatus.openai = 'error'
    }

    // Test Google AI via Cloud Function
    try {
      // Don't actually generate an image for health check (expensive)
      // Just mark as configured if the function exists
      healthStatus.google = this.generateAIImageFn ? 'healthy' : 'not_configured'
    } catch (error) {
      console.error('Google AI health check failed:', error)
      healthStatus.google = 'error'
    }

    return healthStatus
  }
}

// Export singleton instance getter
export default AIAPIService 